// RevertStatusModal — Feature 1.
//
// Confirms a status revert before hitting POST /skip/jobs/:enc_id/
// revert-status. The server walks the audit log (most recent
// status_changed activity row's old_value) to determine the target
// status, so the client doesn't need to know the previous value
// in advance — only the user's intent. Modal copy is intentionally
// generic about the "previous" status.
//
// This modal can be opened from any non-terminal job stage; the
// detail page hides the trigger button when the job is collected /
// completed / cancelled (matching the server's 409 guard).

import { useState } from 'react';

import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { useRevertJobStatus } from '../../hooks/useSkipJobs';
import { extractErrorMessage } from '../../../../utils/errorMessage';

import styles from './ProcessActionModal.module.css';

export interface RevertStatusModalProps {
  isOpen: boolean;
  jobEncId: string;
  jobNumber: string;
  currentStatus: string;
  onSuccess?: () => void;
  onClose: () => void;
}

export function RevertStatusModal({
  isOpen,
  jobEncId,
  jobNumber,
  currentStatus,
  onSuccess,
  onClose,
}: RevertStatusModalProps): JSX.Element | null {
  const revert = useRevertJobStatus(jobEncId);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  function handleSubmit(): void {
    setError(null);
    revert.mutate(undefined, {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <Modal
      open
      onClose={revert.isPending ? () => undefined : onClose}
      title="Revert Job Status"
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose} disabled={revert.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleSubmit} loading={revert.isPending}>
            Revert Status
          </Button>
        </div>
      }
    >
      <p className={styles.body}>
        This will revert job <strong>{jobNumber}</strong> from{' '}
        <strong>'{currentStatus}'</strong> back to its previous status.
        The audit log will be preserved.
      </p>
      <p className={styles.note}>
        ⚠ This action cannot itself be undone.
      </p>
      {error ? <p className={styles.error}>{error}</p> : null}
    </Modal>
  );
}
